import {
  InferenceBillingInfo,
  InferenceContext,
  InferenceContextType,
} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/inference/types';
import React, {ReactNode, useCallback, useContext} from 'react';
import {useParams} from 'react-router-dom';

import {AccountSelectorContext} from '../components/Search/SearchNav/AccountSelectorContextProvider';
import {Account, AccountType} from '../components/Search/SearchNav/types';
import {envIsLocal} from '../config';
import {
  useProjectFromNameQuery,
  useUpsertModelMutation,
} from '../generated/graphql';
import {useViewer} from '../state/viewer/hooks';
import {doNotRetryContext, propagateErrorsContext} from '../util/errors';
import {AccessOption} from '../util/permissions';
import {useRampFlagInferenceCatalog} from '../util/rampFeatureFlags';
import {useGetInferenceUsageDetails} from '../util/usage/inference';
import {
  formatAsDollar,
  getFormattedBillingDate,
} from './Billing/AccountSettings/BillingTab/PlanCard/PlanCardComponents';
import {
  DEFAULT_PROJECT_NAME,
  useSelectedAccountDefaultEntity,
  useUserSpecifiedDefaultEntity,
} from './inferenceHooks';

const useInferenceBillingInfo = (
  selectedAccount: Account | null
): InferenceBillingInfo | null => {
  const {
    billingPeriodStart,
    billingPeriodEnd,
    usage,
    limits,
    loading,
    error,
    isUsagePaid,
  } = useGetInferenceUsageDetails(selectedAccount);

  if (!loading && !error && selectedAccount) {
    return {
      accountPlanType: selectedAccount.accountPlanType,
      billingPeriodStart,
      billingPeriodEnd,
      usage,
      inferenceFreeLimit: limits.inferenceFreeLimit,
      inferenceSafetyLimit: limits.inferenceSafetyLimit,
      isUsagePaid,
      formatAsDollar,
      getFormattedBillingDate,
    };
  }

  return null;
};

type InferenceProviderProps = {
  children: ReactNode;
};

export const InferenceProvider: React.FC<InferenceProviderProps> = ({
  children,
}) => {
  const viewer = useViewer();
  const isLoggedIn = !!viewer;

  let availabilityMessage = '';
  // AccountSelectorContext may still point to last account after logout
  const {selectedAccount} = useContext(AccountSelectorContext);
  // Note: There is such a thing as a hidden personal account, but users
  // can't select it in the account selector so we don't need to handle that.
  const isPersonalAccount =
    isLoggedIn && selectedAccount?.accountType === AccountType.Personal;
  // On local/dedicated accounts will be AccountType.Personal
  const isNonLocalPersonalAccount = !envIsLocal && isPersonalAccount;

  // Note: Organization flag will always be false for personal entities.
  const isInferenceRampEnabled = useRampFlagInferenceCatalog(
    selectedAccount?.name ?? ''
  );
  const isCatalogEnabled =
    !isLoggedIn || isNonLocalPersonalAccount || isInferenceRampEnabled;
  const isInferenceEnabled =
    isInferenceRampEnabled || isNonLocalPersonalAccount;
  if (!isLoggedIn) {
    availabilityMessage = 'You must be logged in to use W&B Inference';
  } else if (!isInferenceEnabled) {
    availabilityMessage = 'W&B Inference is not available for this account';
  }

  // Note: Limits will be wrong for personal accounts,
  // there is a hardcoded limit in the Inference Gateway that is not
  // currently accessible to the frontend. Rather than hardcode a limit
  // here too and try to keep it in sync we rely on explicit checks
  // for the personal entity case when deciding what UI to show.
  const billingInfo = useInferenceBillingInfo(selectedAccount);

  // For now, we don't expect the page to be mounted under an entity/project.
  // But keeping this logic in case that changes and for consistency with quickstart logic.
  const params = useParams<{entityName?: string; projectName?: string}>();
  const defaultEntityName = useUserSpecifiedDefaultEntity();
  const selectedAccountDefaultEntity = useSelectedAccountDefaultEntity();
  // `targetEntityForProjectCreation` is distinct from `entityName` because
  // it takes into account the currently selected account in the account selector.
  // This is the entity for which any quickstart CTAs should create new projects,
  // or for which the quickstart should be run. The Weave quickstart uses this pattern
  // as of this writing, but the Models quickstart uses `entityName`. I (Tim) did not
  // want to change Models quickstart behavior.
  // Precedence: (URL-specified) entityName > selectedAccountDefaultEntity > defaultEntityName)
  const targetEntityForProjectCreation =
    params.entityName ??
    selectedAccountDefaultEntity ??
    defaultEntityName ??
    '';

  const targetProject = params.projectName ?? DEFAULT_PROJECT_NAME;

  // TODO: Should be able to skip this query if we have params.projectName
  const playgroundProjectQuery = useProjectFromNameQuery({
    variables: {
      entityName: targetEntityForProjectCreation,
      projectName: targetProject,
    },
  });

  const projectExists = playgroundProjectQuery.loading
    ? undefined
    : !!playgroundProjectQuery.data?.project?.id;

  const [upsertProjectMutation] = useUpsertModelMutation({
    context: propagateErrorsContext(),
    ...doNotRetryContext(),
  });

  const ensureProjectExists = useCallback(async () => {
    if (!projectExists) {
      await upsertProjectMutation({
        variables: {
          entityName: targetEntityForProjectCreation,
          name: targetProject,
          access: AccessOption.Private,
          description: '',
        },
      });
    }
  }, [
    projectExists,
    upsertProjectMutation,
    targetEntityForProjectCreation,
    targetProject,
  ]);

  const value: InferenceContextType = {
    isLoggedIn,
    isEnvLocal: envIsLocal,
    isCatalogEnabled,
    isInferenceEnabled,
    availabilityMessage,
    playgroundEntity: targetEntityForProjectCreation,
    playgroundProject: targetProject,
    projectExists,
    ensureProjectExists,
    billing: billingInfo,
  };

  return (
    <InferenceContext.Provider value={value}>
      {children}
    </InferenceContext.Provider>
  );
};
